import { ApiClientError } from "@gettactic/api/src/endpoints/response/ApiClientError";

const GENERIC_DEDICATE_ERROR = "Could not dedicate the resource to this user";
const GENERIC_SAVE_ERROR = "Sorry we couldn't save the changes";

/** The `error.code` of an API error body, whatever shape the caller holds it in. */
export const apiErrorCode = (result: unknown): string | undefined => {
  if (typeof result !== "object" || result === null || !("error" in result)) {
    return undefined;
  }
  const { error } = result;
  if (typeof error !== "object" || error === null || !("code" in error)) {
    return undefined;
  }
  return typeof error.code === "string" ? error.code : undefined;
};

/** The code of an error thrown by the API client (calls without `notThrowWithInvalidResponses`). */
export const thrownApiErrorCode = (error: unknown): string | undefined =>
  error instanceof ApiClientError
    ? apiErrorCode(error.response?.result)
    : undefined;

/**
 * True only when the person already owns another resource: the one 409 that
 * moving them (a retry with `confirm_move`) resolves. The other 409s say this
 * resource has no free seat, and moving the person would not help.
 */
export const isMoveConflict = (response: {
  status: number;
  result: unknown;
}): boolean =>
  response.status === 409 &&
  apiErrorCode(response.result) === "user_already_has_dedicated_resource";

/** A readable reason for a failed dedicated-owner change or capacity save. */
export const dedicatedUserErrorMessage = (code: string | undefined): string => {
  switch (code) {
    case "resource_full":
      return "Every seat is taken. Increase the capacity or remove an owner first.";
    case "dedicated_user_already_assigned":
      return "This desk already has an owner. Remove them first.";
    case "capacity_below_dedicated_users":
      return "Capacity can't be lower than the number of owners. Remove owners first.";
    default:
      return GENERIC_DEDICATE_ERROR;
  }
};

/** The toast for a failed General save, which throws on a 4xx. */
export const resourceSaveErrorMessage = (error: unknown): string => {
  const code = thrownApiErrorCode(error);
  return code === "capacity_below_dedicated_users"
    ? dedicatedUserErrorMessage(code)
    : GENERIC_SAVE_ERROR;
};
